import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Bell, ChevronLeft, ChevronRight, Send } from "lucide-react";
import { api, type ReminderLogRow } from "../api";
import { fmtDateTime } from "../lib/format";
import { Button } from "../components/ui/button";
import { Mono, StatusChip } from "../components/ui/ledger";
import { Card, CardContent } from "../components/ui/card";
import { EmptyState } from "../components/ui/empty-state";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { PageFilterPopover } from "../components/page-filter-popover";
import { usePageHeader } from "../lib/use-page-header";

const STATUS_VALUES = ["all", "pending", "sent", "failed", "undeliverable"];

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation("reminders");
  if (status === "sent") return <StatusChip tone="ok">{t("status.sent")}</StatusChip>;
  if (status === "failed")
    return <StatusChip tone="danger">{t("status.failed")}</StatusChip>;
  if (status === "pending")
    return <StatusChip tone="warn">{t("status.pending")}</StatusChip>;
  if (status === "undeliverable")
    return <StatusChip tone="danger">{t("status.undeliverable")}</StatusChip>;
  return <StatusChip tone="muted">{status}</StatusChip>;
}

export function RemindersPage() {
  const { t } = useTranslation("reminders");
  const [items, setItems] = useState<ReminderLogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sendingId, setSendingId] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .reminders({
        status: status === "all" ? undefined : status,
        date_from: from || undefined,
        date_to: to || undefined,
        page: String(page),
        page_size: "30",
      })
      .then((r) => {
        setItems(r.items);
        setTotal(r.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, status, from, to, reloadKey]);

  async function sendNow(id: number) {
    setSendingId(id);
    try {
      await api.sendReminderNow(id);
      toast.success(t("toast.sent"));
      setReloadKey((k) => k + 1);
    } catch (e) {
      const err = e as { code?: string; message?: string };
      const msg =
        err.code === "client_no_telegram"
          ? t("toast.noTelegram")
          : err.message || t("toast.sendFailed");
      toast.error(msg);
    } finally {
      setSendingId(null);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / 30));
  const filterCount = (from ? 1 : 0) + (to ? 1 : 0);
  const clearFilters = () => {
    setFrom("");
    setTo("");
    setPage(1);
  };

  const filterNode = useMemo(
    () => (
      <PageFilterPopover count={filterCount} onClear={clearFilters}>
        <div className="space-y-1.5">
          <Label htmlFor="from">{t("filter.from")}</Label>
          <Input
            id="from"
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="to">{t("filter.to")}</Label>
          <Input
            id="to"
            type="date"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </PageFilterPopover>
    ),
    [from, to, filterCount],
  );

  usePageHeader({ filters: filterNode, filterCount });

  return (
    <div className="reminders-page space-y-4">
      <Tabs
        value={status}
        onValueChange={(v) => {
          setStatus(v);
          setPage(1);
        }}
      >
        <TabsList className="overflow-x-auto">
          {STATUS_VALUES.map((value) => (
            <TabsTrigger key={value} value={value}>
              {t(`status.${value}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Bell />}
              title={t("empty.title")}
              description={t("empty.description")}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("table.date")}</TableHead>
                  <TableHead>{t("table.client")}</TableHead>
                  <TableHead>{t("table.channel")}</TableHead>
                  <TableHead>{t("table.status")}</TableHead>
                  <TableHead>{t("table.text")}</TableHead>
                  <TableHead className="text-right">{t("table.action")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => {
                  const isPending = r.source === "reminder" && r.status === "pending";
                  const hasTelegram = r.client_telegram_id != null;
                  const isUndeliverable = r.status === "undeliverable";
                  const canSend = isPending && hasTelegram;
                  return (
                    <TableRow key={`${r.source}-${r.id}`}>
                      <TableCell className="whitespace-nowrap">
                        <Mono className="text-muted-foreground">
                          {fmtDateTime(r.sent_at || r.due_at || r.created_at)}
                        </Mono>
                      </TableCell>
                      <TableCell className="font-medium">
                        {r.client_name}
                      </TableCell>
                      <TableCell>
                        {isUndeliverable ? (
                          <StatusChip tone="muted" dot={false}>
                            {t("channel.notConnected")}
                          </StatusChip>
                        ) : r.channel === "telegram" ? (
                          <StatusChip tone="muted" dot={false}>
                            {t("channel.telegram")}
                          </StatusChip>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={r.status} />
                      </TableCell>
                      <TableCell className="text-muted-foreground max-w-sm truncate">
                        {r.text || "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {canSend && (
                          <Button
                            size="sm"
                            className="reminder-send-button"
                            disabled={sendingId === r.id}
                            onClick={() => sendNow(r.id)}
                          >
                            <Send />
                            {sendingId === r.id ? t("action.sending") : t("action.send")}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
        <div className="flex items-center justify-between gap-2 p-3 border-t text-sm text-muted-foreground">
          <span>{t("pagination.total", { total })}</span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p - 1)}
              disabled={page <= 1}
            >
              <ChevronLeft />
              {t("pagination.prev")}
            </Button>
            <span className="tabular-nums px-2">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= totalPages}
            >
              {t("pagination.next")}
              <ChevronRight />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
